/**
 * LCV Sensor Validation CrossFrame Listeners
 *
 * Handles CrossFrame requests for weight sensor validation operations.
 * Validates required parameters and delegates to the Gateway client via getServiceClient().
 *
 * 9 listeners mapping to IhmWeightSensorValidationServiceLambda operations:
 * - createLiveLCVWorkflow (scanSensor)
 * - getBaseState
 * - getWeightChange
 * - completeLiveLCVWorkflow (completeWorkflow)
 * - getLatestWorkflow
 * - viewStatus (viewPreviousRunsStatus)
 * - fixError
 * - incrementAttempt
 * - cutTT
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'
import {
    CreateLiveLCVWorkflowInput,
    CreateLiveLCVWorkflowOutput,
    GetBaseStateInput,
    GetBaseStateOutput,
    GetWeightChangeInput,
    GetWeightChangeOutput,
    CompleteLiveLCVWorkflowInput,
    CompleteLiveLCVWorkflowOutput,
    GetLatestWorkflowInput,
    GetLatestWorkflowOutput,
    ViewStatusInput,
    ViewStatusOutput,
    FixErrorInput,
    FixErrorOutput,
    IncrementAttemptInput,
    IncrementAttemptOutput,
    CutTTInput,
    CutTTOutput,
} from '@amzn/coral_com-amazon-ihmweightsensorvalidationservicelambda'
import { validateRequired } from './listenerUtils'

export async function createLiveLCVWorkflowListener (message: any): Promise<CreateLiveLCVWorkflowOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId')
    const client = getServiceClient()
    return client.createLiveLCVWorkflow(CreateLiveLCVWorkflowInput.clone(params)).toPromise()
}

export async function getBaseStateListener (message: any): Promise<GetBaseStateOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId')
    const client = getServiceClient()
    return client.getBaseState(GetBaseStateInput.clone(params)).toPromise()
}

export async function getWeightChangeListener (message: any): Promise<GetWeightChangeOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId')
    const client = getServiceClient()
    return client.getWeightChange(GetWeightChangeInput.clone(params)).toPromise()
}

export async function completeLiveLCVWorkflowListener (message: any): Promise<CompleteLiveLCVWorkflowOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId', 'executionStatus')
    const client = getServiceClient()
    return client.completeLiveLCVWorkflow(CompleteLiveLCVWorkflowInput.clone(params)).toPromise()
}

export async function getLatestWorkflowListener (message: any): Promise<GetLatestWorkflowOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId')
    const client = getServiceClient()
    return client.getLatestWorkflow(GetLatestWorkflowInput.clone(params)).toPromise()
}

export async function viewStatusListener (message: any): Promise<ViewStatusOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId')
    const client = getServiceClient()
    return client.viewStatus(ViewStatusInput.clone(params)).toPromise()
}

export async function fixErrorListener (message: any): Promise<FixErrorOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId')
    const client = getServiceClient()
    return client.fixError(FixErrorInput.clone(params)).toPromise()
}

export async function incrementAttemptListener (message: any): Promise<IncrementAttemptOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId')
    const client = getServiceClient()
    return client.incrementAttempt(IncrementAttemptInput.clone(params)).toPromise()
}

export async function cutTTListener (message: any): Promise<CutTTOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'sensorId', 'workflowId')
    const client = getServiceClient()
    return client.cutTT(CutTTInput.clone(params)).toPromise()
}
